import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  useGetPublicCardsQuery,
  useGetPublicFeaturedContentQuery
} from 'api/publicApi'
import { gt, inc, isNil, length, not, prop, propEq } from 'ramda'
import { useCurrentDomain } from 'hooks'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'

import CardAnimationWrapper from 'components/common/CardAnimationWrapper'
import LoadMoreButton from 'components/common/LoadMoreButton'
import MasonryWrapper from 'components/common/MasonryWrapper'
import PreparedCard from 'components/common/PreparedCardConnector'
import parse from 'html-react-parser'
import BreadCrumbs from '../components/BreadCrumbs'
import { useIntl } from 'react-intl'
import PageHeroBanner from 'components/common/PageHeroBanner'

const GroupPage = () => {
  const { groupId } = useParams()
  const { currentDomain } = useCurrentDomain()
  const [page, setPage] = useState(1)
  const intl = useIntl()

  const { data: content } = useGetPublicFeaturedContentQuery(currentDomain)

  const isMobile = useMediaQuery('(max-width: 600px)')
  const isTablet = useMediaQuery('(max-width: 900px)')

  // Find the group that matches the groupId from the URL
  // need to parse int since groupId is a string
  const group = prop('groups', content)
    ? prop('groups', content).find(propEq(parseInt(groupId), 'id'))
    : null

  const { data, isFetching } = useGetPublicCardsQuery(
    `org_domain=${currentDomain}&filter[content_groups]=${groupId}&page[take]=${
      page * 15
    }`,
    { skip: isNil(content) }
  )

  return (
    <>
      {prop('hero_image_url', group) && (
        <Box
          sx={{
            background: `url(${prop('hero_image_url', group)})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            height: 294,
            width: '100%'
          }}
        />
      )}
      <Stack
        alignItems="center"
        pb={4}
        pt={3}
        px={isMobile ? 2 : isTablet ? 6 : 19}
        spacing={3}
      >
        <Box px={1.2}>
          <BreadCrumbs
            links={[
              {
                href: '/',
                label: intl.formatMessage({
                  defaultMessage: 'Home',
                  id: 'ejEGdx'
                })
              },
              {
                href: `/public/group/${groupId}`,
                label: prop('display_text', group)
              }
            ]}
          />
          <Typography alignSelf="start" sx={{ pb: 3 }} variant="h1">
            {prop('display_text', group)}
          </Typography>
          {
            /* Group description - only render if body_copy exists */
            prop('body_copy', group) && (
              <Box
                sx={{
                  borderRadius: '2px',
                  borderTop: '4px solid',
                  borderTopColor: 'school.secondary',
                  boxShadow: '0 2px 4px 0 rgba(79, 79, 79, 0.25)',
                  px: 2,
                  py: 1
                }}
              >
                <Typography
                  color="text.secondary"
                  fontFamily="mark-ot"
                  fontWeight={400}
                  lineHeight="140%"
                  textAlign="left"
                  variant="subtitle4"
                >
                  {parse(
                    prop('body_copy', group) ? prop('body_copy', group) : ''
                  )}
                </Typography>
              </Box>
            )
          }
        </Box>
        <MasonryWrapper>
          {data?.map((card, index) => (
            <CardAnimationWrapper key={index}>
              <PreparedCard card={card} isPublic />
            </CardAnimationWrapper>
          ))}
        </MasonryWrapper>
        {not(gt(length(data) % 15, 0)) && (
          <LoadMoreButton
            handleClick={() => setPage(inc(page))}
            loading={isFetching}
          />
        )}
      </Stack>
    </>
  )
}

export default GroupPage
